import 'antd-mobile/es/global';
import type { RangePickerProps as AntdRangePickerProps } from 'antd/lib/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { DatePickerDropdownStyles, StyledDatePicker } from './desktopStyles';
import { PresetsDatePicker, RangeCalenderProps } from './types';
import { useTheme } from '@src/theme';
import FormItem from 'antd/es/form/FormItem';
import { RangeCalendarFooter } from './components';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

export const DesktopRangeCalendar = ({
    presets = PresetsDatePicker,
    desktopValue,
    desktopDefaultValue,
    name,
    label,
    layoutLabel = 'vertical',
    required,
    allowClear = true,
    disabled = false,
    format = 'YYYY-MM-DD',
    status,
    size = 'middle',
    placement = 'bottomLeft',
    className,
    style,
    singleMonthView,
    isWithoutFooter,
    open,
    disabledDate,
    renderFooter,
    onChange,
    onOpenChange,
    onDesktopChange,
    ...props
}: RangeCalenderProps) => {
    const { theme } = useTheme();

    const [selection, setSelection] = useState<[Dayjs, Dayjs] | null>(
        desktopValue ?? desktopDefaultValue ?? null
    );
    const [tempSelection, setTempSelection] = useState<[Dayjs, Dayjs] | null>(
        selection
    );

    const [openPicker, setOpenPicker] = useState(false);
    const [pickerValue, setPickerValue] = useState<[Dayjs, Dayjs] | undefined>([
        dayjs().subtract(1, 'month'),
        dayjs(),
    ]);

    useEffect(() => {
        const applied = desktopValue ?? desktopDefaultValue;
        if (applied) {
            setSelection(applied);
            setTempSelection(applied);
        }
    }, [desktopValue, desktopDefaultValue]);

    useEffect(() => {
        if (tempSelection && tempSelection[0]) {
            setPickerValue([
                dayjs(tempSelection[0]).subtract(1, 'month'),
                dayjs(tempSelection[0]),
            ]);
        }
    }, [tempSelection]);
    const handleCalendarChange: AntdRangePickerProps['onCalendarChange'] = (
        dates
    ) => {
        if (Array.isArray(dates)) {
            setTempSelection(
                dates[0] && dates[1] ? [dates[0], dates[1]] : null
            );
            if (dates[0] && !dates[1]) {
                setPickerValue([
                    dayjs(dates[0]).subtract(1, 'month'),
                    dayjs(dates[0]),
                ]);
            }
        }

        if (isWithoutFooter) {
            onDesktopChange?.(
                Array.isArray(dates) && dates[0] && dates[1]
                    ? [dates[0], dates[1]]
                    : null
            );
        }
    };
    const handlePanelChange = (dates) => {
        setPickerValue(dates);
    };
    const handleChange: AntdRangePickerProps['onChange'] = (dates) => {
        if (dates === null) {
            setSelection(null);
            setTempSelection(null);
            onDesktopChange?.(null);
            onChange?.(null);
            return;
        }

        if (Array.isArray(dates)) {
            setTempSelection(
                dates[0] && dates[1] ? [dates[0], dates[1]] : null
            );
            if (dates[0])
                setPickerValue([dates[0].subtract(1, 'month'), dates[0]]);
        }
    };

    const handleCancel = () => {
        setTempSelection(selection);
        setOpenPicker(false);
        if (onOpenChange) {
            onOpenChange(false, true);
        }
    };

    const handleApply = () => {
        setSelection(tempSelection);
        onDesktopChange?.(tempSelection);
        onChange?.(tempSelection);
        setOpenPicker(false);
    };

    return (
        <FormItem
            name={name}
            layout={layoutLabel}
            label={label}
            required={required}
            className="mb-0"
        >
            <DatePickerDropdownStyles />
            <StyledDatePicker
                {...props}
                disabled={disabled}
                allowClear={allowClear}
                placement={placement}
                status={status}
                size={size}
                format={format}
                disabledDate={disabledDate}
                className={className}
                style={style}
                open={open !== undefined ? open : openPicker}
                onOpenChange={onOpenChange || setOpenPicker}
                defaultValue={desktopDefaultValue as [Dayjs, Dayjs]}
                value={tempSelection}
                onCalendarChange={handleCalendarChange}
                onPanelChange={handlePanelChange}
                onChange={handleChange}
                showNow={false}
                presets={presets}
                pickerValue={pickerValue}
                defaultPickerValue={
                    (desktopDefaultValue as [Dayjs, Dayjs]) || [
                        dayjs().subtract(1, 'month'),
                        dayjs(),
                    ]
                }
                inputReadOnly
                suffixIcon={
                    <button
                        className="bg-btnLightFilled rounded-sm p-1"
                        onClick={() => setOpenPicker(true)}
                    >
                        <CalendarDaysIcon
                            className="w-4 h-4 text-primary"
                            color={theme.semantic.textPrimary}
                        />
                    </button>
                }
                dropdownClassName="my-range-picker-dropdown"
                panelRender={(menu) => (
                    <div className="my-range-picker-dropdown">
                        {menu}
                        {!isWithoutFooter &&
                            (renderFooter ? (
                                renderFooter()
                            ) : (
                                <RangeCalendarFooter
                                    onApply={handleApply}
                                    onCancel={handleCancel}
                                />
                            ))}
                    </div>
                )}
                popupClassName={singleMonthView ? 'dateRangePicker' : ''}
            />
        </FormItem>
    );
};
